import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import {
  deleteOrganization,
  loadOrganizations,
  updateOrganization,
} from "../../../queries/organization";
import {
  removeItem,
  updateObjectInArray,
} from "../../../redux/reducers/helpers";
import ReactPaginate from "react-paginate";
import Select from "react-select";
import { useErrorHandler } from "react-error-boundary";
import PropTypes from "prop-types";

const OrganizationsPage = ({ queryClient }) => {
  const handleError = useErrorHandler();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [verified, setVerified] = useState("");
  const [sort, setSort] = useState("DESC");
  const [totalPages, setTotalPages] = useState(0);

  const fetchOrganizations = async (
    page = 1,
    searchTerm = "",
    verified = "",
    sort = "DESC"
  ) =>
    loadOrganizations(page, searchTerm, verified, sort)
      .then((data) => {
        setTotalPages(data?.meta?.total_pages || 0);
        setPage(data?.meta?.page || 1);
        return data.results;
      })
      .catch((error) => {
        handleError(error);
      });

  const {
    isLoading,
    isError,
    data: organizations,
    isPreviousData,
  } = useQuery(
    ["organization", page, searchTerm, verified, sort],
    () =>
      fetchOrganizations(page, searchTerm, verified, sort).then((data) => {
        return data;
      }),
    {
      keepPreviousData: true,
    }
  );

  const verifyOrg = useMutation((org) => {
    return updateOrganization(checkOrgIsVerified(org), org.id).then((data) => {
      queryClient.setQueryData(
        ["organization", page, searchTerm, verified, sort],
        updateObjectInArray(organizations, data)
      );
    });
  });

  const deleteOrg = useMutation((org) => {
    if (confirm(`Are you sure that you want to delete ${org.legalName} ?`)) {
      return deleteOrganization(org.id).then((data) => {
        queryClient.setQueryData(
          ["organization", page, searchTerm, verified, sort],
          removeItem(organizations, data)
        );
      });
    }
  });

  const checkOrgIsVerified = (org) => {
    org.verified = !org.verified;
    return org;
  };

  const verifyButtonIsActive = (verified) => {
    if (verified) {
      return "active";
    } else {
      return "";
    }
  };

  const handlePageClick = (e) => {
    if (!isPreviousData) {
      setPage(e.selected + 1);
    }
  };

  const handleKeyPress = (e) => {
    const searchInput = e.target.value.trim();
    if (e.key === "Enter") {
      fetchOrganizations(page, searchInput, verified, sort);
    }
  };
  const handleSearchInputChange = (e) => {
    e.persist();
    const searchInput = e.target.value.trim();
    if (searchInput.length > 2 || searchInput.length === 0) {
      setTimeout(() => {
        setSearchTerm(searchInput);
      }, 500);
    }
  };

  useEffect(() => {}, []);

  return (
    <div className="container">
      <div className="d-flex align-items-center mb-45">
        <h3 className="m-0">
          <span className="d-none d-sm-inline">Organizations</span>
        </h3>
        <div className="btn-group ml-auto">
          <Link to="/organizations/new" className="btn btn-sm btn-main">
            Create New
          </Link>
          {/*<button*/}
          {/*  type="button"*/}
          {/*  className="btn btn-sm btn-main px-2 dropdown-toggle dropdown-toggle-white dropdown-toggle-split"*/}
          {/*  data-toggle="dropdown"*/}
          {/*  aria-haspopup="true"*/}
          {/*  aria-expanded="false"*/}
          {/*>*/}
          {/*  <span className="sr-only">Toggle Dropdown</span>*/}
          {/*</button>*/}
          {/*<div className="dropdown-menu dropdown-menu-right p-2">*/}
          {/*  <a*/}
          {/*    href=""*/}
          {/*    className="dropdown-item"*/}
          {/*    data-toggle="modal"*/}
          {/*    data-target="#password-reset-modal"*/}
          {/*  >*/}
          {/*    Force global password reset*/}
          {/*  </a>*/}
          {/*  <a className="dropdown-item" href="#">*/}
          {/*    Other global action*/}
          {/*  </a>*/}
          {/*</div>*/}
        </div>
      </div>
      <div className="row row-xs align-items-center mb-4">
        <div className="col-12 col-md-4 col-xl-2 mb-2">
          <input
            className="fin-control fin-control-sm"
            type="search"
            placeholder="Search organizations"
            onChange={(e) => handleSearchInputChange(e)}
            onKeyPress={(e) => handleKeyPress(e)}
          />
        </div>
        <Select
          className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2"
          classNamePrefix="select"
          isClearable={true}
          placeholder={"Status"}
          options={[
            { value: true, label: "Verified" },
            { value: false, label: "Not Verified" },
          ]}
          onChange={(status) => setVerified(status?.value)}
        />
        <Select
          className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2"
          classNamePrefix="select"
          isClearable={true}
          placeholder={"Sort By"}
          options={[
            { value: "asc", label: "Alphabetically (ASC)" },
            { value: "desc", label: "Alphabetically (DESC)" },
          ]}
          onChange={(sortBy) => setSort(sortBy?.value)}
        />
      </div>

      <table className="table-list">
        <thead>
          <tr>
            <th>ORG NAME</th>
            {/*<th>API CALLS</th>*/}
            <th>USERS</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {!isLoading && !isError && (
            <>
              {organizations.map((org) => (
                <tr key={org.id}>
                  <td>
                    <h5 className="text-small m-0">
                      {org.legalName}{" "}
                      {org.verified ? (
                        <strong className="badge text-success">VERIFIED</strong>
                      ) : (
                        <strong className="badge text-error">
                          NOT VERIFIED
                        </strong>
                      )}
                    </h5>
                  </td>
                  <td>
                    <p className="text-small">
                      Admins:{" "}
                      <strong className="text-link-alt">
                        {org.userCounters.admins}
                      </strong>
                      , Developers:{" "}
                      <strong className="text-link-alt">
                        {" "}
                        {org.userCounters.developers}
                      </strong>
                    </p>
                  </td>
                  <td className="table-actions nowrap">
                    <a
                      className={`btn-verify-icon ${verifyButtonIsActive(
                        org.verified
                      )} ml-n2 ml-md-0`}
                      data-toggle="tooltip"
                      data-placement="top"
                      title="Verify"
                      onClick={() =>
                        verifyOrg.mutate({ id: org.id, verified: org.verified })
                      }
                    >
                      <i className="icon-verify" />
                    </a>
                    <Link
                      to={`/organizations/${org.id}/edit`}
                      className="btn-transparent btn-icon ml-n2 ml-md-0"
                      data-toggle="tooltip"
                      data-placement="top"
                      title="Edit"
                    >
                      <i className="icon-edit" />
                    </Link>
                    <Link
                      to={`/organizations/${org.id}/invites`}
                      className="btn-transparent btn-icon ml-n2 ml-md-0"
                      data-toggle="tooltip"
                      data-placement="top"
                      title="Organization invites"
                    >
                      <i className="icon-envelope" />
                    </Link>
                    <a
                      href=""
                      className="btn-transparent btn-icon"
                      data-tooltip="tooltip"
                      data-placement="top"
                      title="Delete"
                      data-toggle="modal"
                      data-target="#delete-modal"
                      onClick={() =>
                        deleteOrg.mutate({
                          id: org.id,
                          legalName: org.legalName,
                        })
                      }
                    >
                      <i className="icon-delete" />
                    </a>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
      {totalPages > 1 && !isLoading && (
        <ReactPaginate
          onPageChange={(e) => handlePageClick(e)}
          previousLabel={"<"}
          nextLabel={">"}
          breakLabel={"..."}
          pageCount={totalPages}
          pageRangeDisplayed={totalPages}
          marginPagesDisplayed={2}
          containerClassName={"pagination mt-45"}
          activeClassName={"active"}
          activeLinkClassName={"current"}
        />
      )}
    </div>
  );
};

OrganizationsPage.propTypes = {
  queryClient: PropTypes.object.isRequired,
};
export default OrganizationsPage;
